import { Fragment, useState } from 'react';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { CopyText } from '@components/copy-text-field';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconButton } from '@components/icon-button';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOutput } from '@components/icons/output';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner, SizedRelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { StepWizard } from '@components/step-wizard';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useUserOption } from 'api/authentication/hooks';
import { useEmailSettings, useRemoveEmailSettings } from 'api/company/hooks';
import { EmailSettings } from 'api/company/requests';

import { useManagedEmailDomainFormSidebar } from './sidebars/managed-form';
import { useUnmanagedEmailDomainFormSidebar } from './sidebars/unmanaged-form';

export const EmailDomain = () => {
  const { t } = useScopedTranslate(locale);
  const emailSettingsQuery = useEmailSettings();

  return (
    <>
      <Header
        actions={
          emailSettingsQuery.isLoading ||
          emailSettingsQuery?.data?.domain ||
          emailSettingsQuery.data?.smtpHost ? undefined : (
            <HeaderActions />
          )
        }
        breadcrumbs={<Breadcrumb items={[{ label: t('emailDomain') }]} />}
      />
      <PageContent>
        {emailSettingsQuery.isLoading && <RelativeSpinner />}
        {emailSettingsQuery.data && <Details emailSettings={emailSettingsQuery.data} />}
      </PageContent>
    </>
  );
};

const HeaderActions = () => {
  const { t } = useScopedTranslate(locale);
  const managedSidebar = useManagedEmailDomainFormSidebar();
  const unmanagedSidebar = useUnmanagedEmailDomainFormSidebar();

  return (
    <Menu
      buttonProps={{
        variant: 'primary',
        iconLeft: <IconEmail />,
        text: t('addCustomDomain'),
        iconRight: <IconDropdown />,
      }}
    >
      <Menu.Label label={t('selectMethod')} />
      <Menu.Item label={t('managed')} onSelect={() => managedSidebar()} />
      <Menu.Item label={t('unmanaged')} onSelect={() => unmanagedSidebar()} />
    </Menu>
  );
};

const Details = ({ emailSettings }: { emailSettings: EmailSettings }) => {
  const { t } = useScopedTranslate(locale);
  const managedFormSidebar = useManagedEmailDomainFormSidebar();
  const unmanagedFormSidebar = useUnmanagedEmailDomainFormSidebar();
  const showRemoveModal = useDeleteModal();
  const isVerified = emailSettings.isManaged
    ? emailSettings.dnsRecords?.every((dnsRecord) => dnsRecord.valid === 'valid')
    : emailSettings.isVerified;

  const isManaged = emailSettings.isManaged;

  const removeMutation = useRemoveEmailSettings();
  const queryClient = useQueryClient();
  const remove = async () => {
    await removeMutation.mutateAsync();
    await queryClient.refetchQueries(['company-email-settings']);
  };
  const showFormSidebar = () =>
    isManaged
      ? managedFormSidebar({
          domain: emailSettings.domain,
          fromAddress: emailSettings.fromAddress,
          fromName: emailSettings.fromName,
        })
      : unmanagedFormSidebar({
          smtpHost: emailSettings.smtpHost,
          smtpPort: emailSettings.smtpPort.toString(),
          smtpLogin: emailSettings.smtpLogin,
          fromAddress: emailSettings.fromAddress,
          fromName: emailSettings.fromName,
          isVerified: emailSettings.isVerified,
        });

  return (
    <Stack space={6}>
      <Text text={t('description')} font="regular14" />
      <Separator />
      <Stack space={4}>
        <Stack space={2}>
          <Text text={t('defaultSenderAddress')} font="bold18" />
          <Text text={t('defaultSenderAddressDescription')} font="regular14" />
        </Stack>
        <Card variant={emailSettings.fromAddress ? 'white' : 'gray'} space={6}>
          <Stack orientation="horizontal" justify="space-between">
            <Stack space={4} orientation="horizontal" align="center">
              <IconEmail />
              {emailSettings.fromAddress ? (
                <div>
                  <Text text={emailSettings.fromName} font="semibold14" />
                  <Text text={emailSettings.fromAddress} font="regular14" />
                </div>
              ) : (
                <Text text={t('noCustomDomain')} font="semibold14" />
              )}
            </Stack>
            {emailSettings.fromAddress && (
              <Stack orientation="horizontal" align="center" space={2}>
                {!emailSettings.isManaged && (
                  <Tag
                    size="small"
                    text={isVerified ? t('dnsVerified') : t('dnsUnverified')}
                    variant={isVerified ? 'green' : 'red'}
                  />
                )}
                <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
                  <Menu.Item
                    label={t('core.edit')}
                    icon={<IconEdit />}
                    onSelect={showFormSidebar}
                  />
                  <Menu.Item
                    label={t('core.remove')}
                    icon={<IconTrash />}
                    onSelect={() =>
                      showRemoveModal({
                        title: t('deleteModalTitle'),
                        description: t('deleteModalDescription'),
                        hasUserConfirmation: true,
                        buttonText: t('core.remove'),
                        buttonIcon: <IconTrash />,
                        onConfirm: remove,
                      })
                    }
                    variant="danger"
                  />
                </Menu>
              </Stack>
            )}
          </Stack>
        </Card>
        {emailSettings.isManaged && emailSettings.fromAddress && (
          <DnsRecords dnsRecords={emailSettings.dnsRecords} isVerified={isVerified ?? false} />
        )}
        {!emailSettings.fromAddress && (
          <Card variant="indigo" space={3}>
            <Stack space={2} orientation="horizontal">
              <IconFormIndicatorInfo color="indigo100" />
              <div>
                <Text text={t('outgoingCommunications')} font="regular14" color="indigo100" />
                <Text text={t('personaliseCommunications')} font="regular14" color="indigo100" />
              </div>
            </Stack>
          </Card>
        )}
        {emailSettings.isManaged && (
          <SetupHelper domain={emailSettings.domain} isVerified={isVerified ?? false} />
        )}
      </Stack>
    </Stack>
  );
};

const SetupHelper = ({ domain, isVerified }: { domain: string; isVerified: boolean }) => {
  const { t } = useScopedTranslate(locale);
  const { value: hasHidden, update } = useUserOption('hasHiddenEmailDomainWizard', false);
  if (hasHidden) {
    return null;
  }
  const steps = [
    {
      id: 'addDomain',
      label: t('addDomain'),
      description: t('addDomainDescription'),
    },
    {
      id: 'copyDNSRecords',
      label: t('copyDNSRecords'),
      description: t('copyDNSRecordsDescription'),
    },
    {
      id: 'refreshAndVerify',
      label: t('refreshAndVerify'),
      description: t('refreshAndVerifyDescription'),
    },
  ] as const;

  const currentStep = isVerified ? 'refreshAndVerify' : domain ? 'copyDNSRecords' : 'addDomain';

  return (
    <StepWizard
      steps={steps}
      currentStep={currentStep}
      title={t('setupHelperHowTo')}
      onDismiss={() => update.mutate(true)}
    />
  );
};

const DnsRecords = ({
  dnsRecords,
  isVerified,
}: {
  dnsRecords: EmailSettings['dnsRecords'];
  isVerified: boolean;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  const handleTestRequest = async () => {
    queryClient.refetchQueries(['company-email-settings']);
  };

  const isFetching = useIsFetching({ queryKey: ['company-email-settings'] }) > 0;

  return (
    <Card space={6} variant="gray">
      <Stack space={4}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Button
            variant="ghost-no-background"
            text={t('dnsRecords')}
            font={'bold14'}
            iconLeft={<IconOutput color="elementPurple50" />}
            onClick={() => setIsCollapsed(!isCollapsed)}
            color="textPrimary"
          />
          <Stack orientation="horizontal" space={4} align="center">
            {isFetching && <SizedRelativeSpinner size={20} />}
            {!isFetching && (
              <>
                <Tag
                  size="small"
                  text={isVerified ? t('dnsVerified') : t('dnsUnverified')}
                  variant={isVerified ? 'green' : 'red'}
                />
                <Button
                  text={t('test')}
                  iconLeft={<IconSend />}
                  variant="ghost"
                  onClick={handleTestRequest}
                />
              </>
            )}
            <Rotate isRotated={!isCollapsed}>
              <IconButton
                variant="ghost-purple"
                icon={<IconDirectionRight />}
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{ cursor: 'pointer' }}
              />
            </Rotate>
          </Stack>
        </Stack>
        <Collapse isCollapsed={isCollapsed}>
          <FieldTable dnsRecords={dnsRecords} />
        </Collapse>
      </Stack>
    </Card>
  );
};

const FieldTable = ({ dnsRecords }: { dnsRecords: EmailSettings['dnsRecords'] }) => {
  const { t } = useScopedTranslate(locale);
  const isFetching = useIsFetching({ queryKey: ['company-email-settings'] });

  return (
    <Grid>
      <Field>
        <Text text={t('status')} font="bold14" caps />
      </Field>
      <Field>
        <Text text={t('hostname')} font="bold14" caps />
      </Field>
      <Field>
        <Text text={t('value')} font="bold14" caps />
      </Field>
      {dnsRecords?.map((dnsRecord) => (
        <Fragment key={dnsRecord.name}>
          <Field>
            <div>
              {isFetching > 0 && <SizedRelativeSpinner size={20} />}
              {isFetching === 0 && (
                <Tag
                  size="small"
                  text={dnsRecord.valid === 'valid' ? t('dnsVerified') : t('dnsUnverified')}
                  variant={dnsRecord.valid === 'valid' ? 'green' : 'red'}
                />
              )}
            </div>
          </Field>
          <Field>
            <Text text={dnsRecord.name} font="regular14" />
          </Field>
          <Field>
            <CopyText value={dnsRecord.value} copyText={t('copy')} copiedText={t('copied')} />
          </Field>
        </Fragment>
      ))}
    </Grid>
  );
};

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr minmax(100px, 4fr)',
});

const Field = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
})({
  '> div': {
    width: '100%',
  },
  padding: pxToRem(8),
  borderBottom: `1px solid ${theme.color.border10}`,
  minHeight: pxToRem(56),
  ':nth-last-child(-n+3)': {
    borderBottom: 'none',
  },
});

const locale = {
  en: {
    addCustomDomain: 'Add custom domain',
    selectMethod: 'Select method',
    managed: 'Managed by us (DNS)',
    unmanaged: 'Managed by you (SMTP)',
    emailDomain: 'Email domain',
    description: 'Manage the email address you want to use for your outbound emails',
    defaultSenderAddress: 'Default sender address',
    defaultSenderAddressDescription: 'Where all outgoing communication will be sent from',
    noCustomDomain: 'No custom domains added',
    outgoingCommunications:
      'All outgoing email communication will be sent from noreply@mg.50skills.com',
    personaliseCommunications:
      'To personalise your communication we recommend using your own domain',
    setupHelperHowTo: 'How to set up your custom domain',
    addDomain: 'Add domain',
    addDomainDescription: 'Click the add domain button and add your details',
    copyDNSRecords: 'Copy DNS records',
    copyDNSRecordsDescription: 'Add DNS record values to your email service provider',
    refreshAndVerify: 'Refresh and verify',
    refreshAndVerifyDescription:
      'Check to see if your records are verified. It can take some time to update DNS records',
    dnsRecords: 'DNS RECORDS (TXT)',
    test: 'Test',
    status: 'Status',
    hostname: 'Hostname',
    value: 'Value',
    dnsVerified: 'Verified',
    dnsUnverified: 'Unverified',
    copy: 'Copy',
    copied: 'Copied',
    deleteModalTitle: 'Remove email domain',
    deleteModalDescription: 'Are you sure you want to remove the email domain?',
    smtp: 'SMTP',
  },
};
